import Modal from "../ui/Modal";
import Button from "../ui/Button";

const formatTimeLabel = (time, hour) => {
  if (typeof time === "string" && time.trim()) return time;
  if (typeof hour === "number") {
    const hh = String(Math.floor(hour)).padStart(2, "0");
    const mm = hour % 1 === 0 ? "00" : "30";
    return `${hh}:${mm}`;
  }
  return "--:--";
};

const toDateTime = (appointment) => {
  const dateStr = appointment?.date || "1970-01-01";
  const timeLabel = formatTimeLabel(appointment?.time, appointment?.hour);
  const [hh, mm] = timeLabel.split(":").map((v) => Number.parseInt(v, 10));
  const date = new Date(dateStr);
  if (Number.isFinite(hh) && Number.isFinite(mm)) {
    date.setHours(hh, mm, 0, 0);
  }
  return date;
};

const getPatientName = (appointment, fallback = "") => {
  if (appointment?.patientName) return appointment.patientName;
  if (appointment?.patient?.lastName || appointment?.patient?.firstName) {
    const full = `${appointment.patient.lastName || ""} ${appointment.patient.firstName || ""}`.trim();
    if (full) return full;
  }
  const full = `${appointment?.lastName || ""} ${appointment?.firstName || ""}`.trim();
  return full || fallback || "Pacient necunoscut";
};

const flattenAppointments = (appointments) => {
  if (!appointments) return [];
  if (Array.isArray(appointments)) return appointments.filter(Boolean);
  if (typeof appointments === "object") {
    return Object.values(appointments)
      .flat()
      .filter(Boolean);
  }
  return [];
};

const getStatusLabel = (status) => {
  if (status === "cancelled") return "Anulată";
  if (status === "rescheduled") return "Reprogramată";
  if (status === "completed") return "Finalizată";
  return "Programată";
};

const getStatusClass = (status) => {
  if (status === "cancelled") return "bg-red-100 text-red-700 border-red-200";
  if (status === "rescheduled") return "bg-yellow-100 text-yellow-700 border-yellow-200";
  if (status === "completed") return "bg-green-100 text-green-700 border-green-200";
  return "bg-blue-100 text-blue-700 border-blue-200";
};

const SLOT_MINUTES = 30;

const formatTimeFromDate = (date) =>
  date.toLocaleTimeString("ro-RO", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

export default function AppointmentsModal({ title, appointments, onClose, patientNameFallback = "" }) {
  const flatList = flattenAppointments(appointments);
  const appointmentLookup = new Map(flatList.map((app) => [app.id, app]));
  const collapsedMap = new Map();

  flatList.forEach((raw) => {
    if (!raw) return;
    const app = { ...raw };
    const mainId = app.mainAppointmentId || app.id;
    if (!mainId) return;

    const startDate = toDateTime(app);
    if (Number.isNaN(startDate.getTime())) return;

    const endDate = new Date(startDate.getTime() + SLOT_MINUTES * 60 * 1000);
    const existing = collapsedMap.get(mainId);

    if (!existing) {
      collapsedMap.set(mainId, {
        mainId,
        app,
        start: startDate,
        end: endDate,
        slots: 1,
      });
      return;
    }

    existing.slots += 1;
    if (startDate < existing.start) {
      existing.start = startDate;
      existing.app = app;
    }
    if (endDate > existing.end) {
      existing.end = endDate;
    }
  });

  const collapsed = Array.from(collapsedMap.values()).sort(
    (a, b) => a.start - b.start
  );

  const grouped = [];
  const groupsMap = new Map();

  collapsed.forEach((entry) => {
    const label = entry.start.toLocaleDateString("ro-RO", {
      weekday: "long",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    if (!groupsMap.has(label)) {
      groupsMap.set(label, { label, items: [] });
      grouped.push(groupsMap.get(label));
    }
    groupsMap.get(label).items.push(entry);
  });

  return (
    <Modal title={title} onClose={onClose} size="lg">
      <div className="max-h-[70vh] overflow-y-auto pr-1 sm:pr-2">
        {grouped.length === 0 ? (
          <p className="text-gray-500 text-sm">Nu există programări.</p>
        ) : (
          grouped.map(({ label, items }) => (
            <div key={label} className="mb-4">
              <h4 className="font-semibold text-blue-600 mb-2 capitalize">{label}</h4>
              <ul className="space-y-2">
                {items.map(({ app, end, slots, mainId }) => {
                  const timeLabel = formatTimeLabel(app.time, app.hour);
                  const endLabel = slots > 1 ? formatTimeFromDate(end) : null;
                  const patientName = getPatientName(app, patientNameFallback);
                  const durationLabel = slots > 1 ? `Durată: ${slots * SLOT_MINUTES} minute` : null;
                  const statusLabel = getStatusLabel(app.status);
                  const statusClass = getStatusClass(app.status);
                  const rescheduledTarget = app.rescheduledTo
                    ? appointmentLookup.get(app.rescheduledTo)
                    : null;

                  return (
                    <li
                      key={mainId}
                      className="p-3 border rounded-lg bg-gray-50 flex flex-col gap-1 text-sm"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-semibold">
                          {timeLabel}
                          {endLabel ? ` – ${endLabel}` : ""} – {patientName}
                        </span>
                        <span className={`text-xs font-semibold px-2 py-1 rounded border ${statusClass}`}>
                          {statusLabel}
                        </span>
                        {(app.doctorDisplayName || app.doctorEmail || app.createdBy) && (
                          <span className="text-xs text-gray-500">
                            Medic: {app.doctorDisplayName || app.doctorEmail || app.createdBy}
                          </span>
                        )}
                      </div>
                      {app.patientCode && (
                        <span className="text-xs text-gray-500">
                          Cod pacient: {app.patientCode}
                        </span>
                      )}
                      {app.phone && (
                        <span className="text-xs text-gray-500">
                          Telefon: {app.phone}
                        </span>
                      )}
                      {app.notes && (
                        <span className="text-xs text-gray-600 whitespace-pre-line">
                          Notițe: {app.notes}
                        </span>
                      )}
                      {app.cancelNote && (
                        <span className="text-xs text-red-600 whitespace-pre-line">
                          Motiv anulare: {app.cancelNote}
                        </span>
                      )}
                      {app.rescheduleNote && (
                        <span className="text-xs text-yellow-700 whitespace-pre-line">
                          Motiv reprogramare: {app.rescheduleNote}
                        </span>
                      )}
                      {rescheduledTarget && (
                        <span className="text-xs text-gray-500">
                          Reprogramată către: {rescheduledTarget.date}{" "}
                          {formatTimeLabel(rescheduledTarget.time, rescheduledTarget.hour)}
                        </span>
                      )}
                      {durationLabel && (
                        <span className="text-xs text-gray-500">{durationLabel}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>

      <div className="flex justify-end mt-4">
        <Button variant="secondary" onClick={onClose}>
          Închide
        </Button>
      </div>
    </Modal>
  );
}
